'use client'

import { useState } from 'react'
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth'
import { auth } from '@/lib/firebase-client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { GlassCard } from '@/components/ui/glass-card'
import { Loader2, Eye, EyeOff } from 'lucide-react'

// Company email domain. Employees log in with just their username and the domain
// is filled in for them; a full address (e.g. an admin on a different domain) can
// still be typed in full, in which case it is used verbatim.
const EMAIL_DOMAIN = '@sashainfinity.com'

function resolveEmail(raw: string): string {
  const v = raw.trim()
  if (!v) return ''
  // Already a full address (contains '@') → leave it alone. Otherwise treat the
  // input as a username and append the company domain.
  return v.includes('@') ? v : `${v}${EMAIL_DOMAIN}`
}

// Firebase returns opaque codes like auth/invalid-credential — translate them
// so users know what went wrong and what to do next.
function getAuthErrorMessage(err: any, fallback: string): string {
  switch (err?.code) {
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'Invalid email or password. Please check your credentials and try again.'
    case 'auth/invalid-email':
      return 'That email address looks invalid. Please check it and try again.'
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please wait a minute before trying again, or use "Forgot Password?" to reset.'
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact your administrator.'
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection and try again.'
    default:
      return err?.message || fallback
  }
}

export function LoginForm() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)
  const [error, setError] = useState('')
  const [infoMsg, setInfoMsg] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Passwords here are admin-set and never contain edge spaces; trimming
    // kills invisible paste artifacts that fail Firebase's exact comparison.
    const trimmedPassword = password.trim()
    setLoading(true)
    setError('')
    setInfoMsg('')

    try {
      await signInWithEmailAndPassword(auth, resolveEmail(email), trimmedPassword)
    } catch (err: any) {
      console.error('Login error:', err)
      setError(getAuthErrorMessage(err, 'Invalid email or password'))
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError('Please enter your email address first.')
      return
    }
    setResetLoading(true)
    setError('')
    setInfoMsg('')
    try {
      await sendPasswordResetEmail(auth, resolveEmail(email))
      setInfoMsg('Password reset email sent! Check your inbox.')
    } catch (err: any) {
      console.error(err)
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        setError('No account found with this email address.')
      } else {
        setError(getAuthErrorMessage(err, 'Failed to send reset email. Please try again.'))
      }
    } finally {
      setResetLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden">
      <GlassCard tone="orange" className="w-full max-w-md p-6 md:p-8">
        <div className="flex flex-col items-center text-center mb-6">
          <div className="mb-4 rounded-2xl bg-neutral-100 dark:bg-white/[0.04] ring-1 ring-inset ring-neutral-200 dark:ring-white/10 p-3 backdrop-blur-md">
            <img
              src="/sasha-logo-small.png"
              alt="Sasha Infinity Logo"
              className="h-16 w-auto"
            />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-white/90">
            Sasha Infinity
          </h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-white/60">
            Company Management System
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-wide text-neutral-500 dark:text-white/60">Email</Label>
            <div className="relative">
              <Input
                id="email"
                type="text"
                inputMode="email"
                autoComplete="username"
                spellCheck={false}
                autoCapitalize="none"
                placeholder="yourname"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={`bg-white dark:bg-white/[0.03] border-neutral-200 dark:border-white/10 text-neutral-900 dark:text-white/90 placeholder:text-neutral-400 dark:placeholder:text-white/40 focus:border-orange-400 focus-visible:ring-orange-400/30 focus-visible:ring-[3px] ${email.includes('@') ? '' : 'pr-[10.5rem]'}`}
              />
              {!email.includes('@') && (
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-neutral-400 dark:text-white/40">
                  {EMAIL_DOMAIN}
                </span>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-xs uppercase tracking-wide text-neutral-500 dark:text-white/60">Password</Label>
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={resetLoading}
                className="text-xs text-orange-600 hover:text-orange-500 font-medium hover:underline disabled:opacity-50 focus:outline-none"
              >
                {resetLoading ? 'Sending...' : 'Forgot Password?'}
              </button>
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required={!resetLoading}
                className="bg-white dark:bg-white/[0.03] border-neutral-200 dark:border-white/10 text-neutral-900 dark:text-white/90 placeholder:text-neutral-400 dark:placeholder:text-white/40 focus:border-orange-400 focus-visible:ring-orange-400/30 focus-visible:ring-[3px] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-neutral-400 hover:text-neutral-600 dark:text-white/40 dark:hover:text-white"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-rose-500/10 ring-1 ring-inset ring-rose-500/30 px-3 py-2">
              <p className="text-sm text-rose-600 dark:text-rose-300 text-center">{error}</p>
            </div>
          )}

          {infoMsg && (
            <div className="rounded-lg bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 px-3 py-2">
              <p className="text-sm text-emerald-600 dark:text-emerald-300 text-center">{infoMsg}</p>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-semibold shadow-lg shadow-orange-500/30 border-0"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        <p className="mt-6 text-center text-[11px] uppercase tracking-wider text-neutral-400 dark:text-neutral-400 dark:text-white/40">
          Secure access · Sasha Infinity Workspace
        </p>
      </GlassCard>
    </div>
  )
}
